package com.anjuros.website.templates.template9.components

import androidx.compose.runtime.Composable
import com.anjuros.website.components.language.LocalText
import com.anjuros.website.templates.template9.modules.Template9Theme
import com.anjuros.website.templates.template9.modules.getColor
import com.anjuros.website.templates.template9.modules.getColorHex
import com.anjuros.website.templates.template9.styles.Template9ButtonStyle
import com.anjuros.website.templates.template9.styles.Template9SectionTitleStyle
import com.anjuros.website.templates.template9.util.ResKey
import com.anjuros.website.templates.template9.util.Template9Constants
import com.anjuros.website.templates.template9.util.Template9Res
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.css.TextTransform
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.letterSpacing
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.modifiers.textDecorationLine
import com.varabyte.kobweb.compose.ui.modifiers.textTransform
import com.varabyte.kobweb.compose.ui.modifiers.whiteSpace
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.CSSColorValue
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text


/** Rounded “pill” button with an optional paw icon – e.g. “Book a Walk”. */
@Composable
fun Template9PrimaryButton(
    text: String,
    path: String,
    modifier: Modifier = Modifier,
    backgroundColor: CSSColorValue = Template9Theme.Primary.getColor(),
    contentColor: CSSColorValue = Colors.White,
    showPaw: Boolean = true,
    small: Boolean = false
) {
    Link(
        modifier = modifier.textDecorationLine(TextDecorationLine.None),
        path = path
    ) {
        Row(
            modifier = Template9ButtonStyle.toModifier()
                .backgroundColor(backgroundColor)
                .borderRadius(50.px)
                .cursor(Cursor.Pointer)
                .padding(
                    leftRight = if (small) 20.px else 26.px,
                    topBottom = if (small) 10.px else 14.px
                )
                .gap(8.px),
            verticalAlignment = Alignment.CenterVertically
        ) {
            if (showPaw) {
                Image(
                    modifier = Modifier.size(if (small) 16.px else 18.px),
                    src = Template9Res.Icon.paw_white,
                    alt = "paw"
                )
            }
            Span(
                attrs = Modifier
                    .fontFamily(Template9Constants.FONT_FAMILY)
                    .fontSize(if (small) 14.px else 16.px)
                    .fontWeight(FontWeight.Bold)
                    .whiteSpace(WhiteSpace.NoWrap)
                    .color(contentColor)
                    .toAttrs()
            ) {
                Text(text)
            }
        }
    }
}

@Composable
fun Template9PrimaryButton(
    text: ResKey,
    path: String,
    modifier: Modifier = Modifier,
    backgroundColor: CSSColorValue = Template9Theme.Primary.getColor(),
    contentColor: CSSColorValue = Colors.White,
    showPaw: Boolean = true,
    small: Boolean = false
) {
    Template9PrimaryButton(
        text = LocalText(text),
        path = path,
        modifier = modifier,
        backgroundColor = backgroundColor,
        contentColor = contentColor,
        showPaw = showPaw,
        small = small
    )
}

/** Outlined pill button with an arrow – e.g. “Learn More →”. */
@Composable
fun Template9OutlineButton(
    text: ResKey,
    path: String,
    modifier: Modifier = Modifier
) {
    Link(
        modifier = modifier.textDecorationLine(TextDecorationLine.None),
        path = path
    ) {
        Row(
            modifier = Template9ButtonStyle.toModifier()
                .backgroundColor(Colors.White)
                .border(width = 2.px, style = LineStyle.Solid, color = Template9Theme.Secondary.rgb)
                .borderRadius(50.px)
                .cursor(Cursor.Pointer)
                .padding(leftRight = 26.px, topBottom = 12.px)
                .gap(10.px),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Span(
                attrs = Modifier
                    .fontFamily(Template9Constants.FONT_FAMILY)
                    .fontSize(16.px)
                    .fontWeight(FontWeight.Bold)
                    .whiteSpace(WhiteSpace.NoWrap)
                    .color(Template9Theme.Secondary.rgb)
                    .toAttrs()
            ) {
                Text(LocalText(text))
            }
            Span(
                attrs = Modifier
                    .fontSize(18.px)
                    .fontWeight(FontWeight.Bold)
                    .color(Template9Theme.Accent.rgb)
                    .toAttrs()
            ) {
                Text("→")
            }
        }
    }
}

/** Small uppercase label framed by two hearts – “♡ ABOUT ME ♡”. */
@Composable
fun Template9Eyebrow(
    text: ResKey,
    modifier: Modifier = Modifier,
    color: CSSColorValue = Template9Theme.Primary.getColor(),
    heartColor: String = Template9Theme.Accent.hex
) {
    Row(
        modifier = modifier.gap(8.px),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Template9HeartIcon(size = 14, color = heartColor)
        P(
            attrs = Modifier
                .margin(0.px)
                .fontFamily(Template9Constants.FONT_FAMILY)
                .fontSize(13.px)
                .fontWeight(FontWeight.Bold)
                .letterSpacing(2.px)
                .textTransform(TextTransform.Uppercase)
                .color(color)
                .toAttrs()
        ) {
            Text(LocalText(text))
        }
        Template9HeartIcon(size = 14, color = heartColor)
    }
}

/** Eyebrow + big rounded heading + hand-drawn underline. */
@Composable
fun Template9SectionTitle(
    eyebrow: ResKey,
    title: ResKey,
    modifier: Modifier = Modifier,
    alignment: Alignment.Horizontal = Alignment.CenterHorizontally,
    titleColor: CSSColorValue = Template9Theme.Secondary.rgb,
    eyebrowColor: CSSColorValue = Template9Theme.Primary.getColor(),
    underlineColor: String = Template9Theme.Primary.getColorHex()
) {
    Column(
        modifier = modifier,
        horizontalAlignment = alignment
    ) {
        Template9Eyebrow(
            text = eyebrow,
            color = eyebrowColor
        )
        P(
            attrs = Template9SectionTitleStyle.toModifier()
                .margin(top = 12.px, bottom = 6.px)
                .fontFamily(Template9Constants.HEADING_FONT_FAMILY, Template9Constants.FONT_FAMILY)
                .fontWeight(FontWeight.Bold)
                .textAlign(if (alignment == Alignment.CenterHorizontally) TextAlign.Center else TextAlign.Start)
                .color(titleColor)
                .toAttrs()
        ) {
            Text(LocalText(title))
        }
        Template9Squiggle(color = underlineColor)
    }
}
